import type { ApiResponseEnvelope } from '@/shared/api/types/api-response'
import type {
  ChangePasswordRequest,
  PaginatedDTO,
  TenantAdminCreateRequest,
  TenantAdminDTO,
} from '@/shared/api/types/user-types'
import { GW, toQueryString } from '@/shared/api/endpoints'
import { buildTenantHeaderConfig } from '@/shared/api/utils/tenant-header'
import { unwrapApiEnvelope } from '@/shared/api/utils/unwrapapi'
import { request } from '@/shared/libs/request'

const USERS_PATH = 'users'
const TENANT_ADMINS_PATH = 'users/admins'
const TENANT_ADMIN_ACCOUNT_LEVEL = 'tenant_admin'

/** IAM caps `limit` at 100; a tenant never has anywhere near that many admins. */
export const TENANT_ADMINS_PAGE_SIZE = 100

/**
 * Lists the admin accounts of a tenant (owner first — IAM orders the owner ahead of the rest).
 *
 * @param tenantId - Tenant to list. Sent as `X-Tenant-Id` for a super admin only (IAM rejects it
 * from anyone else); for an owner the token's own tenant applies.
 */
export async function getTenantAdmins(tenantId: string): Promise<PaginatedDTO<TenantAdminDTO>> {
  const qs = toQueryString({
    skip: 0,
    limit: TENANT_ADMINS_PAGE_SIZE,
    accountLevel: TENANT_ADMIN_ACCOUNT_LEVEL,
  })
  const response = await request.get<ApiResponseEnvelope<PaginatedDTO<TenantAdminDTO>>>(
    GW.iam(`${USERS_PATH}?${qs}`),
    buildTenantHeaderConfig(tenantId),
  )
  return unwrapApiEnvelope(response.data)
}

/**
 * Adds a (non-owner) admin to a tenant. Only a super admin, or the tenant's owner, is allowed;
 * IAM answers 403 for any other admin and 409 when the username is taken in that tenant.
 */
export async function createTenantAdmin(
  tenantId: string,
  payload: TenantAdminCreateRequest,
): Promise<TenantAdminDTO> {
  const response = await request.post<ApiResponseEnvelope<TenantAdminDTO>>(
    GW.iam(TENANT_ADMINS_PATH),
    payload,
    buildTenantHeaderConfig(tenantId),
  )
  return unwrapApiEnvelope(response.data)
}

/**
 * Sets a new password for an admin of `tenantId`. IAM allows a super admin, or the tenant's
 * owner for a non-owner admin; any other caller gets 403 and an unknown/foreign admin gets 404.
 * The endpoint answers with an empty payload, so there is nothing to unwrap.
 */
export async function changeTenantAdminPassword(
  tenantId: string,
  adminId: string,
  payload: ChangePasswordRequest,
): Promise<void> {
  await request.post(
    GW.iam(`${USERS_PATH}/${adminId}/change-password`),
    payload,
    buildTenantHeaderConfig(tenantId),
  )
}
